import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES } from '@lms/shared-types';
import { Alert, Button, Loader } from '@lms/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { contractSigningPath } from '@/lib/student-contract-gate';
import { LegalPolicyConsentField } from './LegalPolicyConsentField';
import type { LegalPolicyConsentItem } from './types';

interface PendingContractResponse {
  data: { id: string } | null;
}

export function AcceptLegalPoliciesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = usePermissions();
  const isStudent = hasRole(ROLES.STUDENT);
  const [acceptedIds, setAcceptedIds] = useState<Set<string>>(() => new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ['legal-policies', 'pending', 'blocksAccess'],
    queryFn: () =>
      api<{ data: LegalPolicyConsentItem[] }>('/legal/policies/pending?blocksAccess=true'),
    enabled: isStudent,
  });

  const { data: contractPending, isLoading: contractLoading } = useQuery({
    queryKey: ['student', 'contracts', 'pending'],
    queryFn: () => api<PendingContractResponse>('/student/contracts/pending'),
    enabled: isStudent,
  });

  const policies = data?.data ?? [];

  const allAccepted = useMemo(() => {
    if (policies.length === 0) return true;
    return policies.every((policy) => acceptedIds.has(policy.id));
  }, [policies, acceptedIds]);

  const acceptMutation = useMutation({
    mutationFn: async (policyIds: string[]) => {
      for (const policyId of policyIds) {
        await api(`/legal/policies/${policyId}/accept`, {
          method: 'POST',
          body: { source: 'standalone' },
        });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['legal-policies'] });
      const pending = await api<PendingContractResponse>('/student/contracts/pending');
      if (pending.data?.id) {
        navigate(contractSigningPath(pending.data.id), { replace: true });
        return;
      }
      navigate('/dashboard', { replace: true });
    },
  });

  function handleToggle(policyId: string, accepted: boolean) {
    setAcceptedIds((prev) => {
      const next = new Set(prev);
      if (accepted) next.add(policyId);
      else next.delete(policyId);
      return next;
    });
  }

  if (!isStudent) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading || contractLoading) {
    return (
      <div className="lms-site-admin-hub-container py-8 md:py-12">
        <Loader label="Cargando políticas pendientes…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="lms-site-admin-hub-container py-8 md:py-12">
        <Alert variant="danger">No se pudieron cargar las políticas pendientes.</Alert>
      </div>
    );
  }

  if (policies.length === 0) {
    const contractId = contractPending?.data?.id;
    if (contractId) {
      return <Navigate to={contractSigningPath(contractId)} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="lms-site-admin-hub-container py-8 md:py-12">
      <div className="lms-legal-policies-accept lms-site-admin-hub-page mx-auto max-w-2xl font-sans">
        <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Aceptación de políticas legales</h1>
        <p className="mt-2 text-dash-on-surface-variant">
          Para continuar usando la plataforma debes revisar y aceptar las siguientes políticas.
        </p>

        <div className="lms-credentials-glass-panel mt-8 rounded-2xl p-6 shadow-sm">
          <LegalPolicyConsentField
            policies={policies}
            acceptedIds={acceptedIds}
            onToggle={handleToggle}
            disabled={acceptMutation.isPending}
          />

          <div className="mt-8 flex justify-end">
            <Button
              type="button"
              disabled={!allAccepted || acceptMutation.isPending}
              loading={acceptMutation.isPending}
              onClick={() => acceptMutation.mutate(policies.map((p) => p.id))}
            >
              Aceptar y continuar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
